import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DatetimeService } from '../../../shared/services/datetime.service';
import { ToastService } from '../../../shared/services/toast.service';
import { EmailMessageApiService } from '../services/email-message-api.service';
import { CorreoDetailModalComponent } from '../correo-detail-modal/correo-detail-modal.component';
import { CorreoRecibidoDetailModalComponent } from '../correo-recibido-detail-modal/correo-recibido-detail-modal.component';
import {
  TimelineItemResponse,
  TimelineSearchParams,
  ContactInboxSyncResponse,
} from '../models/email-message.model';

/**
 * Timeline unificado de correos con un contacto: ENVIADOS al contacto +
 * RECIBIDOS de él, ordenados cronológicamente DESC. Consume
 * GET /api/emails/timeline/by-contact/{contactId} (RBAC server-side; el
 * frontend NO pasa flags). Se incrusta como pestaña "Correos" del detalle
 * de un contacto.
 *
 * Click en una fila:
 *  - sourceType=EMAIL_MESSAGE (enviado) → modal de detalle real (GET /api/emails/{sourceId}).
 *  - sourceType=INBOX_MESSAGE (recibido) → modal de detalle del recibido
 *    (GET /api/emails/inbox-message/{sourceId}), con cuerpo completo.
 *
 * Refresh on-demand (cache-first + sync en segundo plano): el job periódico de
 * sync IMAP fue desactivado. Al abrir la pestaña se muestra de inmediato el
 * timeline cacheado (GET) y EN PARALELO se dispara el sync (POST .../sync) sin
 * bloquear la UI. Solo si el sync trae recibidos nuevos (synced && newMessages>0)
 * se recarga el GET. El botón "Refrescar" repite ese flujo manualmente.
 */
@Component({
  selector: 'app-contacto-correos',
  standalone: true,
  imports: [CommonModule, CorreoDetailModalComponent, CorreoRecibidoDetailModalComponent],
  templateUrl: './contacto-correos.component.html',
})
export class ContactoCorreosComponent implements OnChanges {
  /** Id del contacto cuyos correos se listan. */
  @Input() contactId: number | null = null;
  /** Cuando es true, dispara la primera carga (lazy desde el tab). */
  @Input() active = false;

  items: TimelineItemResponse[] = [];
  loading = false;
  error = '';
  private cargado = false;

  /** Indicador sutil de "actualizando…" mientras corre el sync (no bloquea la lista). */
  syncing = false;
  /** Hint MUY sutil opcional cuando el buzón IMAP no está conectado. */
  noInboxHint = false;

  // Paginación (shape del contrato del repo)
  globalPage = 0;
  globalTotalPages = 0;
  globalTotalElements = 0;
  hasNext = false;
  hasPrevious = false;
  readonly pageSize = 15;

  // Modal de detalle de un ENVIADO (reusa correo-detail-modal).
  showDetailModal = false;
  selectedEmailId: number | null = null;

  // Modal de detalle de un RECIBIDO (correo-recibido-detail-modal).
  showReceivedModal = false;
  selectedInboxMessageId: number | null = null;

  constructor(
    private emailApi: EmailMessageApiService,
    private datetime: DatetimeService,
    private toast: ToastService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['contactId']) {
      // Cambió el contacto → reset y recarga si el tab está activo.
      this.cargado = false;
      this.items = [];
      this.globalPage = 0;
      this.noInboxHint = false;
    }
    if ((changes['active'] || changes['contactId']) && this.active && this.contactId != null && !this.cargado) {
      // Cache-first: muestra de inmediato lo cacheado y dispara el sync en paralelo.
      this.cargarTimeline();
      this.dispararSync();
    }
  }

  /**
   * Carga el timeline cacheado (GET). En modo `silent` no muestra el spinner
   * grande (se usa para recargas en segundo plano tras un sync con novedades).
   */
  cargarTimeline(silent = false): void {
    if (this.contactId == null) return;
    if (!silent) this.loading = true;
    this.error = '';
    this.cargado = true;

    const params: TimelineSearchParams = {
      page: this.globalPage,
      size: this.pageSize,
    };

    this.emailApi.getContactTimeline(this.contactId, params).subscribe({
      next: (res) => {
        this.items = res.content;
        this.globalPage = res.page ?? this.globalPage;
        this.globalTotalPages = res.totalPages;
        this.globalTotalElements = res.totalElements;
        this.hasNext = res.hasNext;
        this.hasPrevious = res.hasPrevious;
        this.loading = false;
      },
      error: (err) => {
        const msg = err.error?.message || 'Error al cargar el historial de correos';
        this.loading = false;
        if (!silent) {
          this.error = msg;
          this.toast.error({ title: 'Error', message: msg });
        }
      },
    });
  }

  /**
   * Dispara el sync IMAP on-demand (POST .../sync) en segundo plano.
   * Recarga el GET timeline SOLO si synced && newMessages > 0.
   * Casos normales (cooldown, sin buzón, sin email, etc.) son silenciosos.
   * Botón "Refrescar" e inicio del tab usan este mismo método.
   */
  refrescar(): void {
    this.dispararSync();
  }

  private dispararSync(): void {
    if (this.contactId == null || this.syncing) return;
    this.syncing = true;

    this.emailApi.syncContactTimeline(this.contactId).subscribe({
      next: (res: ContactInboxSyncResponse) => {
        this.syncing = false;
        this.manejarResultadoSync(res);
      },
      error: (err) => {
        // Error HTTP real (401/403/400): toast como en el resto del repo.
        this.syncing = false;
        const msg = err.error?.message || 'No se pudo actualizar la bandeja';
        this.toast.error({ title: 'Error', message: msg });
      },
    });
  }

  private manejarResultadoSync(res: ContactInboxSyncResponse): void {
    // Solo recargar si hubo recibidos nuevos.
    if (res.synced && res.newMessages > 0) {
      this.noInboxHint = false;
      this.globalPage = 0; // los nuevos son los más recientes (DESC)
      this.cargarTimeline(true);
      return;
    }

    // Casos sin recarga. Manejo silencioso salvo SYNC_ERROR (toast discreto).
    switch (res.reason) {
      case 'NO_IMAP_CONNECTION':
        // Hint MUY sutil opcional; no es un toast.
        this.noInboxHint = true;
        break;
      case 'SYNC_ERROR':
        this.toast.error({ title: 'Error', message: 'No se pudo actualizar la bandeja' });
        break;
      // COOLDOWN / NO_CONTACT_EMAIL / CONTACT_NOT_FOUND / null → silencioso.
      default:
        break;
    }
  }

  cambiarPagina(page: number): void {
    if (page < 0 || page >= this.globalTotalPages) return;
    this.globalPage = page;
    this.cargarTimeline();
  }

  // ── Interacción al click ──

  abrirItem(item: TimelineItemResponse): void {
    if (item.sourceType === 'EMAIL_MESSAGE') {
      this.selectedEmailId = item.sourceId;
      this.showDetailModal = true;
    } else {
      // INBOX_MESSAGE: detalle completo del recibido (GET /api/emails/inbox-message/{sourceId}).
      this.selectedInboxMessageId = item.sourceId;
      this.showReceivedModal = true;
    }
  }

  cerrarModal(): void {
    this.showDetailModal = false;
    this.selectedEmailId = null;
  }

  cerrarReceived(): void {
    this.showReceivedModal = false;
    this.selectedInboxMessageId = null;
  }

  // ── Helpers de dirección ──

  isSent(item: TimelineItemResponse): boolean {
    return item.direction === 'SENT';
  }

  /** Punto/negrita sutil para recibidos no leídos. */
  isUnread(item: TimelineItemResponse): boolean {
    return item.direction === 'RECEIVED' && item.isRead === false;
  }

  // ── Badge de estado (solo SENT) ──

  statusColor(status: string): string {
    switch (status) {
      case 'SENT':
      case 'DELIVERED':
        return 'bg-success-50 text-success-700 dark:bg-success-500/15 dark:text-success-400';
      case 'QUEUED':
      case 'SENDING':
      case 'DEFERRED':
        return 'bg-warning-50 text-warning-700 dark:bg-warning-500/15 dark:text-orange-400';
      case 'FAILED':
      case 'BOUNCED':
      case 'COMPLAINED':
        return 'bg-error-50 text-error-700 dark:bg-error-500/15 dark:text-error-500';
      default:
        return 'bg-gray-100 text-gray-700 dark:bg-white/5 dark:text-white/80';
    }
  }

  statusLabel(status: string): string {
    const map: Record<string, string> = {
      QUEUED: 'En cola',
      SENDING: 'Enviando',
      SENT: 'Enviado',
      DELIVERED: 'Entregado',
      FAILED: 'Fallido',
      BOUNCED: 'Rebotado',
      DEFERRED: 'Diferido',
      COMPLAINED: 'Spam',
    };
    return map[status] || status;
  }

  formatDate(iso?: string | null): string {
    return this.datetime.formatDateTimeShort(iso);
  }

  get pageDisplay(): number {
    return this.globalPage + 1;
  }
}
